import {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useState,
} from 'react';
import type { AvatarConfig, PaletteColors, SpriteGrid } from './avatarTypes';
import {
  SPRITES_BY_ID,
  EYES_BY_ID,
  MOUTHS_BY_ID,
  ACCESSORIES_BY_ID,
  BACKGROUNDS_BY_ID,
} from './avatarData';

export interface AvatarCanvasHandle {
  getImageBlob: (scale?: number) => Promise<Blob | null>;
  getCanvas: () => HTMLCanvasElement | null;
}

interface AvatarCanvasProps {
  config: AvatarConfig;
  /** Pixel size of each grid cell on screen */
  pixelScale?: number;
  /** Animation offsets injected by the animation controller */
  animationOffsets?: { breathY: number; blinkFrame: boolean };
  className?: string;
  /** Callback when a pixel is clicked (row, col) — enables pixel editing */
  onPixelClick?: (row: number, col: number) => void;
  /** Whether pixel editing mode is active (shows crosshair + hover highlight) */
  pixelEditing?: boolean;
}

/** Map palette index → hex color */
function indexToColor(index: number, palette: PaletteColors): string | null {
  switch (index) {
    case 0: return null; // transparent
    case 1: return palette.bodyPrimary;
    case 2: return palette.bodySecondary;
    case 3: return palette.eyeColor;
    case 4: return palette.mouthColor;
    case 5: return palette.accessoryColor;
    default: return null;
  }
}

/** Draw a sprite grid onto a canvas context at (offsetX, offsetY) */
function drawGrid(
  ctx: CanvasRenderingContext2D,
  grid: SpriteGrid,
  palette: PaletteColors,
  scale: number,
  offsetX: number,
  offsetY: number,
) {
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      const idx = grid[row][col];
      const color = indexToColor(idx, palette);
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(
        offsetX + col * scale,
        offsetY + row * scale,
        scale,
        scale,
      );
    }
  }
}

/** Draw a tiled background pattern */
function drawBackground(
  ctx: CanvasRenderingContext2D,
  grid: SpriteGrid,
  bgColor: string,
  patternColor: string,
  canvasW: number,
  canvasH: number,
  scale: number,
) {
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, canvasW, canvasH);

  if (grid.length === 0) return;
  const tileH = grid.length;
  const tileW = grid[0].length;

  ctx.fillStyle = patternColor;
  // Dim the pattern
  ctx.globalAlpha = 0.15;
  for (let py = 0; py < canvasH; py += tileH * scale) {
    for (let px = 0; px < canvasW; px += tileW * scale) {
      for (let row = 0; row < tileH; row++) {
        for (let col = 0; col < tileW; col++) {
          if (grid[row][col] > 0) {
            ctx.fillRect(px + col * scale, py + row * scale, scale, scale);
          }
        }
      }
    }
  }
  ctx.globalAlpha = 1;
}

/** Apply pixel overrides as a final paint layer */
function drawPixelOverrides(
  ctx: CanvasRenderingContext2D,
  overrides: Record<string, string>,
  scale: number,
) {
  for (const [key, color] of Object.entries(overrides)) {
    const [r, c] = key.split(',').map(Number);
    ctx.fillStyle = color;
    ctx.fillRect(c * scale, r * scale, scale, scale);
  }
}

const AvatarCanvas = forwardRef<AvatarCanvasHandle, AvatarCanvasProps>(
  function AvatarCanvas({ config, pixelScale = 16, animationOffsets, className, onPixelClick, pixelEditing }, ref) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [hoverCell, setHoverCell] = useState<{ row: number; col: number } | null>(null);
    const isPaintingRef = useRef(false);

    const render = useCallback(
      (ctx: CanvasRenderingContext2D, scale: number) => {
        const gridSize = config.gridSize;
        const canvasW = gridSize * scale;
        const canvasH = gridSize * scale;
        const palette = config.palette;

        // Clear
        ctx.clearRect(0, 0, canvasW, canvasH);

        // Background
        const bgDef = BACKGROUNDS_BY_ID[config.background];
        if (bgDef && bgDef.grid.length > 0) {
          drawBackground(
            ctx, bgDef.grid, palette.backgroundColor,
            palette.bodyPrimary, canvasW, canvasH, scale,
          );
        } else {
          ctx.fillStyle = palette.backgroundColor;
          ctx.fillRect(0, 0, canvasW, canvasH);
        }

        // Breathing offset
        const breathY = animationOffsets?.breathY ?? 0;

        // Base sprite
        const sprite = SPRITES_BY_ID[config.entityType];
        if (!sprite) return;
        drawGrid(ctx, sprite.grid, palette, scale, 0, breathY * scale);

        // Eyes overlay
        const eyeDef = EYES_BY_ID[config.eyes];
        if (eyeDef) {
          const [ar, ac] = sprite.anchorPoints.eyes;
          if (animationOffsets?.blinkFrame) {
            const blinkGrid = eyeDef.grid.map((row, ri) =>
              ri === 0 ? row : row.map(() => 0),
            );
            drawGrid(ctx, blinkGrid, palette, scale, ac * scale, (ar + breathY) * scale);
          } else {
            drawGrid(ctx, eyeDef.grid, palette, scale, ac * scale, (ar + breathY) * scale);
          }
        }

        // Mouth overlay
        const mouthDef = MOUTHS_BY_ID[config.mouth];
        if (mouthDef) {
          const [ar, ac] = sprite.anchorPoints.mouth;
          drawGrid(ctx, mouthDef.grid, palette, scale, ac * scale, (ar + breathY) * scale);
        }

        // Accessory overlay
        const accDef = ACCESSORIES_BY_ID[config.accessory];
        if (accDef && accDef.grid.length > 0) {
          const [ar, ac] = sprite.anchorPoints.accessory;
          drawGrid(ctx, accDef.grid, palette, scale, ac * scale, (ar + breathY) * scale);
        }

        // Pixel overrides (final paint layer)
        if (config.pixelOverrides) {
          drawPixelOverrides(ctx, config.pixelOverrides, scale);
        }
      },
      [config, animationOffsets],
    );

    // Draw on every config or animation change
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const size = config.gridSize * pixelScale;
      canvas.width = size;
      canvas.height = size;
      ctx.imageSmoothingEnabled = false;
      render(ctx, pixelScale);

      // Draw hover highlight for pixel editing
      if (pixelEditing && hoverCell) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 2;
        ctx.strokeRect(
          hoverCell.col * pixelScale + 1,
          hoverCell.row * pixelScale + 1,
          pixelScale - 2,
          pixelScale - 2,
        );
      }
    }, [config, pixelScale, animationOffsets, render, pixelEditing, hoverCell]);

    useImperativeHandle(ref, () => ({
      getImageBlob: async (scale = 4) => {
        const exportSize = config.gridSize * scale;
        const offscreen = document.createElement('canvas');
        offscreen.width = exportSize;
        offscreen.height = exportSize;
        const ctx = offscreen.getContext('2d');
        if (!ctx) return null;
        ctx.imageSmoothingEnabled = false;
        const sprite = SPRITES_BY_ID[config.entityType];
        if (!sprite) return null;

        const palette = config.palette;

        // Background
        const bgDef = BACKGROUNDS_BY_ID[config.background];
        if (bgDef && bgDef.grid.length > 0) {
          drawBackground(ctx, bgDef.grid, palette.backgroundColor, palette.bodyPrimary, exportSize, exportSize, scale);
        } else {
          ctx.fillStyle = palette.backgroundColor;
          ctx.fillRect(0, 0, exportSize, exportSize);
        }

        // Base
        drawGrid(ctx, sprite.grid, palette, scale, 0, 0);

        // Eyes
        const eyeDef = EYES_BY_ID[config.eyes];
        if (eyeDef) {
          const [ar, ac] = sprite.anchorPoints.eyes;
          drawGrid(ctx, eyeDef.grid, palette, scale, ac * scale, ar * scale);
        }

        // Mouth
        const mouthDef = MOUTHS_BY_ID[config.mouth];
        if (mouthDef) {
          const [ar, ac] = sprite.anchorPoints.mouth;
          drawGrid(ctx, mouthDef.grid, palette, scale, ac * scale, ar * scale);
        }

        // Accessory
        const accDef = ACCESSORIES_BY_ID[config.accessory];
        if (accDef && accDef.grid.length > 0) {
          const [ar, ac] = sprite.anchorPoints.accessory;
          drawGrid(ctx, accDef.grid, palette, scale, ac * scale, ar * scale);
        }

        // Pixel overrides
        if (config.pixelOverrides) {
          drawPixelOverrides(ctx, config.pixelOverrides, scale);
        }

        return new Promise<Blob | null>((resolve) => {
          offscreen.toBlob(resolve, 'image/png');
        });
      },
      getCanvas: () => canvasRef.current,
    }));

    const size = config.gridSize * pixelScale;

    const getCell = useCallback(
      (e: React.PointerEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const col = Math.floor(x / pixelScale);
        const row = Math.floor(y / pixelScale);
        if (row >= 0 && row < config.gridSize && col >= 0 && col < config.gridSize) {
          return { row, col };
        }
        return null;
      },
      [pixelScale, config.gridSize],
    );

    const handlePointerDown = useCallback(
      (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!onPixelClick || !pixelEditing) return;
        isPaintingRef.current = true;
        (e.target as Element).setPointerCapture(e.pointerId);
        const cell = getCell(e);
        if (cell) onPixelClick(cell.row, cell.col);
      },
      [onPixelClick, pixelEditing, getCell],
    );

    const handlePointerMove = useCallback(
      (e: React.PointerEvent<HTMLCanvasElement>) => {
        const cell = getCell(e);
        if (pixelEditing) {
          setHoverCell(cell);
        }
        if (isPaintingRef.current && onPixelClick && cell) {
          onPixelClick(cell.row, cell.col);
        }
      },
      [pixelEditing, onPixelClick, getCell],
    );

    const handlePointerUp = useCallback(() => {
      isPaintingRef.current = false;
    }, []);

    const handlePointerLeave = useCallback(() => {
      isPaintingRef.current = false;
      setHoverCell(null);
    }, []);

    return (
      <canvas
        ref={canvasRef}
        className={className}
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          imageRendering: 'pixelated',
          cursor: pixelEditing ? 'crosshair' : undefined,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
      />
    );
  },
);

export default AvatarCanvas;
